import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AnimatedSection } from "@/components/AnimatedSection";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const offerings = [
  { title: "Diagnóstico financeiro", highlight: "completo" },
  { title: "Mapeamento do", highlight: "balanço patrimonial" },
  { title: "Organização do", highlight: "fluxo de caixa" },
  { title: "Plano financeiro orientado a", highlight: "objetivos" },
];

const OfferingsCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(4);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setItemsPerView(1);
      } else if (window.innerWidth < 768) {
        setItemsPerView(2);
      } else if (window.innerWidth < 1024) {
        setItemsPerView(3);
      } else {
        setItemsPerView(4);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const maxIndex = offerings.length - itemsPerView;

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(maxIndex, prev + 1));
  };

  const visibleOfferings = offerings.slice(currentIndex, currentIndex + itemsPerView);

  return (
    <section id="o-que-oferecemos" className="py-16 sm:py-20 md:py-24 bg-[#FAFAFA]">
      <div className="container-wide">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left side - Title */}
          <div className="lg:col-span-4">
            <AnimatedSection>
              <span className="text-sm text-[#112711]/60 mb-3 block">O que você recebe no planejamento</span>
              <div className="w-12 h-1 bg-[#112711] mb-6" />
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-medium text-[#112711] leading-tight" style={{ textShadow: '0 0 20px rgba(17, 39, 17, 0.3)' }}>
                Tudo o que você precisa para sair do improviso e entrar no controle.
              </h2>
            </AnimatedSection>
          </div>

          {/* Right side - Carousel + CTA */}
          <div className="lg:col-span-8">
            {/* Carousel Items */}
            <div className="relative">
              <div className="overflow-hidden">
                <motion.div 
                  className="flex gap-4"
                  animate={{ x: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <AnimatePresence mode="popLayout">
                    {visibleOfferings.map((item, idx) => {
                      const actualIndex = currentIndex + idx;
                      return (
                        <motion.div
                          key={actualIndex}
                          initial={{ opacity: 0, x: 50 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -50 }}
                          transition={{ duration: 0.3, delay: idx * 0.05 }}
                          className="flex-1 min-w-0"
                        >
                          <div className="border-l-2 border-[#112711]/20 pl-4 py-2">
                            <span className="text-2xl sm:text-3xl font-serif font-medium text-primary block mb-1">
                              {String(actualIndex + 1).padStart(2, "0")}
                            </span>
                            <p className="text-sm sm:text-base text-[#112711]">
                              {item.title}{" "}
                              <span className="font-semibold">{item.highlight}</span>
                            </p>
                          </div>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </motion.div>
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between mt-8">
                {/* Step indicator */}
                <span className="text-sm text-[#112711]/60">
                  {currentIndex + 1}/{offerings.length - itemsPerView + 1}
                </span>

                {/* Navigation buttons */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={handlePrev}
                    disabled={currentIndex === 0}
                    className="w-12 h-12 rounded-full bg-[#112711] hover:bg-[#112711]/80 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-all duration-300 shadow-lg"
                    aria-label="Anterior"
                  >
                    <ChevronLeft className="w-6 h-6 text-white" />
                  </button>
                  <button
                    onClick={handleNext}
                    disabled={currentIndex >= maxIndex}
                    className="w-12 h-12 rounded-full bg-[#112711] hover:bg-[#112711]/80 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-all duration-300 shadow-lg"
                    aria-label="Próximo"
                  >
                    <ChevronRight className="w-6 h-6 text-white" />
                  </button>
                </div>
              </div>
            </div>

            {/* CTA Card - Below carousel */}
            <AnimatedSection delay={0.2}>
              <Link 
                to="/contato"
                className="group block mt-8 p-4 rounded-xl bg-[#112711]/85 hover:bg-[#112711] transition-colors duration-300"
                style={{ boxShadow: '0 0 12px rgba(243, 222, 44, 0.15), inset 0 0 0 1px rgba(243, 222, 44, 0.2)' }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-xl sm:text-2xl font-serif font-medium text-white" style={{ textShadow: '0 0 15px rgba(255, 255, 255, 0.5)' }}>
                      Fale conosco
                    </span>
                    <span className="text-white/90 text-sm">
                      e descubra como podemos ajudar
                    </span>
                  </div>
                  <ArrowRight className="w-5 h-5 text-white/80 group-hover:text-primary group-hover:translate-x-1 transition-all duration-300" />
                </div>
              </Link>
            </AnimatedSection>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OfferingsCarousel;
